import { ApiError } from '../../api/client';
import type { PartnerMaterialInput, PartnerMaterialKind, PartnerMaterialResult } from '../../api/partner';

export interface MaterialIntent {
  owner: number;
  key: string;
  kind: PartnerMaterialKind;
  input: PartnerMaterialInput;
  phase: 'unknown' | 'prepared' | 'accepted';
  receipt?: PartnerMaterialResult;
}
const storageKey = (owner: number) => `steos:partner:material:${owner}`;
export function readMaterialIntent(owner: number): MaterialIntent | null {
  const raw = sessionStorage.getItem(storageKey(owner));
  if (!raw) return null;
  if (raw.length > 16384) throw new Error('material_intent_invalid');
  const value = JSON.parse(raw) as MaterialIntent;
  if (
    !value ||
    value.owner !== owner ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.key) ||
    !['story', 'banner', 'poster', 'reel'].includes(value.kind) ||
    !['unknown', 'prepared', 'accepted'].includes(value.phase) ||
    !value.input ||
    typeof value.input !== 'object' ||
    Array.isArray(value.input)
  )
    throw new Error('material_intent_invalid');
  for (const [key, field] of Object.entries(value.input)) {
    if (!['template_id', 'theme', 'caption'].includes(key) || typeof field !== 'string' || field.length > (key === 'caption' ? 200 : 64))
      throw new Error('material_intent_invalid');
  }
  if (!value.input.template_id || !value.input.theme) throw new Error('material_intent_invalid');
  if (value.phase !== 'unknown' && (!value.receipt || materialPhase(value.kind, value.receipt) !== value.phase))
    throw new Error('material_intent_invalid');
  return value;
}
export const saveMaterialIntent = (intent: MaterialIntent) => sessionStorage.setItem(storageKey(intent.owner), JSON.stringify(intent));
export const clearMaterialIntent = (owner: number) => sessionStorage.removeItem(storageKey(owner));
export function safeMaterialUrl(value?: string) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? value : null;
  } catch {
    return null;
  }
}
export function materialPhase(kind: PartnerMaterialKind, receipt: PartnerMaterialResult): MaterialIntent['phase'] {
  if (kind === 'story' && safeMaterialUrl(receipt.media_url)) return 'prepared';
  if (
    kind === 'banner' &&
    receipt.state === 'ready' &&
    typeof receipt.prepared_message_id === 'string' &&
    receipt.prepared_message_id.length > 0
  )
    return 'prepared';
  if ((kind === 'poster' && receipt.delivery_job_id) || (kind === 'reel' && receipt.order?.id && receipt.status === 'accepted'))
    return 'accepted';
  return 'unknown';
}
export function definitiveMaterialError(error: unknown) {
  return (
    error instanceof ApiError &&
    [400, 403, 404, 409, 422].includes(error.status) &&
    ['material_input_invalid', 'material_kind_invalid', 'material_template_not_found', 'materials_unavailable'].includes(error.code)
  );
}
